import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EditLink, IconChevron } from '../../components/icons/WizardIcons';
import { CCR_EXPAND_SECTION_EVENT } from '../../utils/collapsibleSection';

/** Intranet-aligned create-case-request step bar (`ccr-step-bar` in `agd.css`). */
export function StepBar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['ccr-step-bar', className].filter(Boolean).join(' ')}>{children}</div>;
}

export function StepSep() {
  return <div className="ccr-sdiv" aria-hidden="true" />;
}

export function WizardStep({
  state,
  numberOrCheck,
  stepLabel,
  name,
  showLabel = true,
  clickable = false,
  onClick,
}: {
  state: 'done' | 'active' | 'pending';
  numberOrCheck: string;
  stepLabel: string;
  name: string;
  /** When false, only the step number/check circle is shown. */
  showLabel?: boolean;
  clickable?: boolean;
  onClick?: () => void;
}) {
  const wrapCls =
    state === 'done' ? 'ccr-si s-done' : state === 'active' ? 'ccr-si s-active' : 'ccr-si';
  const content = (
    <>
      <div className="ccr-sc">{numberOrCheck}</div>
      {showLabel ? (
        <div className="ccr-st">
          <div className="ccr-slbl">{stepLabel}</div>
          <div className="ccr-snm">{name}</div>
        </div>
      ) : null}
    </>
  );

  if (clickable && onClick) {
    return (
      <button type="button" className={`${wrapCls} ccr-si-btn`} onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className={wrapCls}>{content}</div>;
}

/** Intranet-aligned section card (`ccr-sec` in `agd.css`). */
export function SectionCard({
  title,
  children,
  defaultOpen = true,
  editTo,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  editTo?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const expand = () => setOpen(true);
    root.addEventListener(CCR_EXPAND_SECTION_EVENT, expand);
    return () => root.removeEventListener(CCR_EXPAND_SECTION_EVENT, expand);
  }, []);

  return (
    <div className="ccr-sec" ref={rootRef}>
      <button type="button" className="ccr-sh" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="ccr-sh-t">{title}</span>
        <span className="ccr-sh-actions">
          {editTo ? <EditLink to={editTo} onClick={(e) => e.stopPropagation()} /> : null}
          <span className="ccr-sh-ic">
            <IconChevron expanded={open} />
          </span>
        </span>
      </button>
      <div className={`ccr-sb${open ? '' : ' is-collapsed'}`}>{children}</div>
    </div>
  );
}
